// Published-content "programme" record for one department — the child-of-
// department object webadmin calls a Program (BE-CSE, MCA, M.Tech — IoT &
// Sensor Systems, ...). Exactly one programme per programme-granting
// department: the 19 published Program records match, one for one, the 19
// non-basic-science departments in departments.ts (PHY/CHE/MAT/SH award no
// degree of their own and have none) — matched via the same
// resolveDepartment() legacy-code mapping publishedDepartmentExtras.ts uses.
//
// Most of a Program record is either identical to what departments.ts
// already carries or empty across every one of the 19 records published so
// far (checked 2026-09-17):
//   - summary/overview byte-match the department's own `about` text (CSE
//     checked in full) — wiring them in would duplicate, not add.
//   - award/awardLabel: 17 of 19 print "B,E." with a stray comma; the site
//     already computes a clean degree line locally (Department.tsx's
//     degreeOf()), so a dirtier duplicate adds nothing.
//   - careerOutcomes, structureStages, syllabus, cover, images, attachments:
//     empty/null on every one of the 19 records.
//
// What's wired, genuinely additive and single-source:
//   duration             always populated, all 19 records
//   eligibility           15 of 19 (the 4 newest M.Tech programmes have none yet)
//   admissionRoutes       16 of 19
//   specialization        null on all 19 today — kept guarded so it appears
//                          the moment webadmin populates one, with no code
//                          change needed
//   affiliatingUniversity  "VTU" on all 19 today — uniform now, but this is
//                          the one fact on the page with no other source at
//                          all, so it's shown even though it doesn't yet
//                          vary department to department
//   advisoryCommittee      populated on 10 of 19 (the programmes deptBoards.ts
//                          also already covers) — IS the Departmental
//                          Advisory Board (confirmed 2026-09-18: BE-CSE's
//                          22-member list matches deptBoards.ts's `cs` dab[]
//                          name-for-name), just thinned to name + a generic
//                          "member" designation instead of the real
//                          role/committeeRole text deptBoards.ts has. The DAB
//                          was hidden from the site by request on 2026-08-10;
//                          the user explicitly chose to wire this thinner
//                          version in anyway under its own name ("Programme
//                          advisory committee"), so it renders alongside
//                          Board of Studies rather than replacing it —
//                          deptBoards.ts's own richer dab[] stays unused.
//   level                  "undergraduate"/"postgraduate" on all 19 —
//                          shown as "Programme level" alongside (not instead
//                          of) the existing UG/PG quick-fact, which is
//                          departments.ts's own record of the same thing.
//   intake.facultyCount /
//   intake.sanctioned      by explicit request, shown as "Programme faculty
//                          count" / "Programme sanctioned intake" — labelled
//                          distinctly from the department-level "Faculty and
//                          staff strength" (our own resolved roster count)
//                          and "Sanctioned intake" (departments.ts's
//                          published stats) quick-facts because they can
//                          disagree: CB's department-level sanctioned intake
//                          is 60, this field says 200 for the same
//                          department. Labelling them separately says "here
//                          are two different published figures", which is
//                          true, rather than quietly picking one.
//
// is_featured, display_order, cover_image_file_id, images[], attachments[],
// seo{}, summary, overview and slug are explicitly NOT used — see the
// reasoning above and the file's own history.
import type { Program } from "@zivin-labs/site-content";
import { useCollection } from "./siteContent";
import { DEPARTMENTS, resolveDepartment, type Department } from "./departments";

export interface PublishedAdvisoryCommitteeMember {
  name: string;
  designation: string;
}

export interface PublishedProgramInfo {
  durationLabel: string | null;
  eligibility: string | null;
  admissionRoutes: string[];
  specialization: string | null;
  affiliatingUniversity: string | null;
  advisoryCommittee: PublishedAdvisoryCommitteeMember[];
  /** "UG" / "PG" — the programme's own record of level, formatted the same way departments.ts's own d.level already is. */
  level: string | null;
  /** From intake.facultyCount — a different figure than the site's own resolved roster count, shown under its own label. */
  facultyCount: number | null;
  /** From intake.sanctioned — a different figure than departments.ts's published stats.sanctionedIntakeUg, shown under its own label. */
  sanctionedIntake: number | null;
  intakeAcademicYear: string | null;
}

const EMPTY: PublishedProgramInfo = {
  durationLabel: null,
  eligibility: null,
  admissionRoutes: [],
  specialization: null,
  affiliatingUniversity: null,
  advisoryCommittee: [],
  level: null,
  facultyCount: null,
  sanctionedIntake: null,
  intakeAcademicYear: null,
};

const LEVEL_LABELS: Record<string, string> = {
  undergraduate: "UG",
  postgraduate: "PG",
};

/** "undergraduate" -> "UG", "postgraduate" -> "PG" — matches the abbreviation departments.ts's own d.level already uses. An unrecognised value falls back to Title Case of its own words rather than a guessed abbreviation. */
function formatLevel(level: string): string {
  return LEVEL_LABELS[level] ?? level.replace(/_/g, " ").replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
}

/** "member" -> "Member" — matches deptBoards.ts's own committeeRole casing ("Member (Nominated by VTU)", etc). */
function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * @param code This department's own identity code — d.code, e.g. "CS" or
 *   "STR" — NEVER d.staffCode. See the same warning in
 *   publishedDepartmentExtras.ts: staffCode groups departments that share
 *   one faculty pool (env, str and cv all carry staffCode "CV"), which is
 *   the wrong key for "which published programme belongs to this page" —
 *   it would match all three to whichever program happens to resolve to
 *   "CV" first.
 */
export function usePublishedProgram(code: string | undefined): PublishedProgramInfo {
  const { items } = useCollection("programs");
  if (!code) return EMPTY;

  const rec = (items as Program[]).find((p) => {
    const resolved = resolveDepartment(p.department?.code);
    const resolvedCode = (resolved?.code ?? "").toUpperCase();
    return resolvedCode === code.toUpperCase();
  });
  if (!rec) return EMPTY;

  return {
    durationLabel: rec.duration?.label || null,
    eligibility: rec.eligibility?.trim() || null,
    admissionRoutes: rec.admissionRoutes ?? [],
    specialization: rec.specialization?.trim() || null,
    affiliatingUniversity: rec.affiliatingUniversity?.trim() || null,
    advisoryCommittee: (rec.advisoryCommittee ?? [])
      .filter((m) => m.name?.trim())
      .map((m) => {
        const designation = m.designation?.trim();
        return { name: m.name.trim(), designation: designation ? capitalize(designation) : "Member" };
      }),
    level: rec.level ? formatLevel(rec.level) : null,
    facultyCount: rec.intake?.facultyCount ?? null,
    sanctionedIntake: rec.intake?.sanctioned ?? null,
    intakeAcademicYear: rec.intake?.asOfAcademicYear?.trim() || null,
  };
}

/** One card of the Academics page's postgraduate section. */
export interface PostgraduateProgramme {
  department: Department;
  /** The programme's name as published; the department's own name for a programme webadmin has not published. */
  name: string;
  /** "M Tech" as published; "MCA" for an unpublished programme, whose card has no published label. */
  awardLabel: string;
  /** Published duration, e.g. "2 years"; null where unpublished. */
  duration: string | null;
  /** Published faculty records for the department — never the hand-kept staff.ts roster. */
  facultyCount: number;
  published: boolean;
}

/**
 * The postgraduate programmes the Academics page lists: webadmin's published
 * postgraduate programmes, in its order, each on its department's page. A
 * postgraduate department in departments.ts with no published programme (MCA
 * at the moment) stays on the list from the local record, after the published
 * ones, rather than dropping a programme the institute runs.
 */
export function usePostgraduateProgrammes(): PostgraduateProgramme[] {
  const programs = useCollection("programs").items as Program[];
  const faculty = useCollection("faculty").items as unknown as Array<{ department?: { code?: string } | null }>;

  const facultyOf = (d: Department) =>
    faculty.filter((f) => resolveDepartment(f.department?.code)?.code === d.code).length;

  const published = programs
    .filter((p) => p.level === "postgraduate")
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
    .flatMap((p): PostgraduateProgramme[] => {
      const department = resolveDepartment(p.department?.code);
      if (!department) return [];
      return [
        {
          department,
          name: p.name,
          awardLabel: p.awardLabel || "",
          duration: p.duration?.label || null,
          facultyCount: facultyOf(department),
          published: true,
        },
      ];
    });

  const local = DEPARTMENTS.filter(
    (d) => d.level === "PG" && !published.some((p) => p.department.code === d.code),
  ).map(
    (department): PostgraduateProgramme => ({
      department,
      name: department.name,
      awardLabel: department.slug === "mca" ? "MCA" : "M.Tech",
      duration: null,
      facultyCount: facultyOf(department),
      published: false,
    }),
  );

  return [...published, ...local];
}
